/**
 * Behaviour for the Relow editorial sections (rs-*).
 * Everything is delegated from the document so it keeps working when the
 * theme editor re-renders a section.
 */
import { CartAddEvent } from '@theme/events';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * @param {HTMLElement} root
 * @returns {number}
 */
function currentIndex(root) {
  return Number(root.dataset.rsIndex || 0);
}

/**
 * Shows one slide inside a [data-rs-slider] and syncs its tabs, dots and counter.
 * @param {HTMLElement} root
 * @param {number} index
 */
function setSlide(root, index) {
  const slides = Array.from(root.querySelectorAll('[data-rs-slide]'));
  if (!slides.length) return;

  const active = ((index % slides.length) + slides.length) % slides.length;

  slides.forEach((slide, position) => {
    const on = position === active;
    slide.classList.toggle('is-active', on);
    slide.toggleAttribute('inert', !on);
    slide.setAttribute('aria-hidden', String(!on));
  });

  root.querySelectorAll('[data-rs-goto]').forEach((control) => {
    const on = Number(control.getAttribute('data-rs-goto')) === active;
    control.classList.toggle('is-active', on);
    control.setAttribute('aria-pressed', String(on));
  });

  const counter = root.querySelector('[data-rs-count]');
  if (counter) counter.textContent = `${active + 1} / ${slides.length}`;

  root.dataset.rsIndex = String(active);
}

document.addEventListener('click', (event) => {
  const control = event.target instanceof Element ? event.target.closest('[data-rs-goto], [data-rs-step]') : null;
  if (!control) return;

  const root = control.closest('[data-rs-slider]');
  if (!(root instanceof HTMLElement)) return;

  root.dataset.rsTouched = 'true';

  if (control.hasAttribute('data-rs-goto')) {
    setSlide(root, Number(control.getAttribute('data-rs-goto')));
  } else {
    setSlide(root, currentIndex(root) + Number(control.getAttribute('data-rs-step')));
  }
});

/* Swipe left and right on touch screens */
let swipeRoot = null;
let swipeX = 0;
let swipeY = 0;

document.addEventListener(
  'touchstart',
  (event) => {
    const root = event.target instanceof Element ? event.target.closest('[data-rs-slider][data-rs-swipe]') : null;
    swipeRoot = root instanceof HTMLElement ? root : null;
    if (!swipeRoot || !event.touches[0]) return;
    swipeX = event.touches[0].clientX;
    swipeY = event.touches[0].clientY;
  },
  { passive: true }
);

document.addEventListener(
  'touchend',
  (event) => {
    if (!swipeRoot || !event.changedTouches[0]) return;
    const deltaX = event.changedTouches[0].clientX - swipeX;
    const deltaY = event.changedTouches[0].clientY - swipeY;

    if (Math.abs(deltaX) > 48 && Math.abs(deltaX) > Math.abs(deltaY) * 1.5) {
      swipeRoot.dataset.rsTouched = 'true';
      setSlide(swipeRoot, currentIndex(swipeRoot) + (deltaX < 0 ? 1 : -1));
    }

    swipeRoot = null;
  },
  { passive: true }
);

/* Auto-advance, only until the visitor interacts, and never with reduced motion */
window.setInterval(() => {
  if (reduceMotion.matches || document.hidden) return;

  document.querySelectorAll('[data-rs-slider][data-rs-auto]').forEach((root) => {
    if (!(root instanceof HTMLElement)) return;
    if (root.dataset.rsTouched === 'true' || root.matches(':hover') || root.contains(document.activeElement)) return;
    setSlide(root, currentIndex(root) + 1);
  });
}, 6000);

/* Theme editor: selecting a block brings its slide forward */
document.addEventListener('shopify:block:select', (event) => {
  const slide = event.target instanceof Element ? event.target.closest('[data-rs-slide]') : null;
  const root = slide ? slide.closest('[data-rs-slider]') : null;
  if (!slide || !(root instanceof HTMLElement)) return;

  root.dataset.rsTouched = 'true';
  setSlide(root, Array.from(root.querySelectorAll('[data-rs-slide]')).indexOf(slide));
});

/* Add to cart from the flavour switcher, using the theme's own cart drawer */
document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-rs-atc')) return;

  event.preventDefault();

  const button = form.querySelector('[type="submit"]');
  const label = form.querySelector('[data-rs-atc-label]');
  const idleText = label ? label.textContent : '';
  let message = form.dataset.addedLabel || 'Added';

  if (button instanceof HTMLButtonElement) button.disabled = true;

  const formData = new FormData(form);
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean);

  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  try {
    const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'text/html' },
      body: formData,
    });
    const data = await response.json();

    if (data.status) {
      message = data.description || data.message || message;
    } else {
      form.dispatchEvent(
        new CartAddEvent({}, String(formData.get('id')), {
          source: 'rs-flavours',
          itemCount: Number(formData.get('quantity')) || 1,
          productId: form.dataset.productId,
          sections: data.sections,
        })
      );
    }
  } catch (error) {
    form.submit();
    return;
  }

  if (label) label.textContent = message;

  window.setTimeout(() => {
    if (label) label.textContent = idleText;
    if (button instanceof HTMLButtonElement) button.disabled = false;
  }, 1800);
});
